/**
 * Query keys — DESIGN.md §13.2.
 *
 * "Query keys per entity + per (viewConfig, cursor) for lists."
 *
 * One module owns every key so SSE invalidation (§15) can target them
 * precisely. A key invented inline in a component is a key the realtime layer
 * does not know how to invalidate, which shows up as "someone else's change
 * didn't appear" — the exact failure §15 exists to prevent.
 */
import type { ViewConfig } from '@slate/shared';

export const queryKeys = {
  lookups: ['lookups'] as const,
  users: ['users'] as const,
  userRefs: ['users', 'refs'] as const,

  projects: ['projects'] as const,
  projectList: (status: string, q: string | null) => ['projects', 'list', status, q] as const,
  project: (key: string) => ['projects', 'byKey', key] as const,
  projectSections: (id: string) => ['projects', id, 'sections'] as const,
  projectStatusUpdates: (id: string) => ['projects', id, 'status-updates'] as const,
  projectAccess: (id: string) => ['projects', id, 'access'] as const,

  tasks: ['tasks'] as const,
  /** A list is identified by its config; two views with the same config share a cache entry. */
  taskQuery: (config: ViewConfig) => ['tasks', 'query', config] as const,
  task: (id: string) => ['tasks', 'detail', id] as const,
  taskByNumber: (number: number) => ['tasks', 'byNumber', number] as const,
  taskComments: (id: string) => ['tasks', id, 'comments'] as const,
  taskActivity: (id: string) => ['tasks', id, 'activity'] as const,
  taskAttachments: (id: string) => ['tasks', id, 'attachments'] as const,
  /**
   * #99 — under `tasks` on purpose: a due-date change invalidates `tasks`,
   * and a reminder relative to the due date has a new next time when it does.
   */
  taskReminders: (id: string) => ['tasks', id, 'reminders'] as const,
  similarTasks: (title: string) => ['tasks', 'similar', title] as const,

  /** Per-user UI memory — collapsed sections (§6.1). */
  uiState: ['ui-state'] as const,

  notifications: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
} as const;
